import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Instagram, Linkedin, Youtube } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { contact, LOGO_URL } from "@/data/content";

const icons = { instagram: Instagram, linkedin: Linkedin, youtube: Youtube };

const Row = ({ icon: Icon, label, value, href, testId }) => (
  <motion.a
    data-testid={testId}
    href={href}
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="group flex items-start gap-5 border-b border-white/5 py-6"
  >
    <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center hex-clip bg-elevated text-turq group-hover:bg-turq group-hover:text-ink transition-colors">
      <Icon size={17} />
    </span>
    <span>
      <span className="block text-[11px] uppercase tracking-[0.3em] text-slate-500">{label}</span>
      <span className="mt-1 block text-base md:text-lg text-slate-200 group-hover:text-glow transition-colors">{value}</span>
    </span>
  </motion.a>
);

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead eyebrow="Contact Us" title={<>Birlikte <span className="text-gradient">üretelim</span></>} testId="contact-title" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <Row icon={MapPin} label="Adres" value={contact.address} href="#contact" testId="contact-address" />
            <Row icon={Phone} label="Telefon" value={contact.phone} href={`tel:${contact.phone.replace(/\s/g, "")}`} testId="contact-phone-link" />
            <Row icon={Mail} label="E-posta" value={contact.email} href={`mailto:${contact.email}`} testId="contact-email-link" />
          </div>

          <div className="flex flex-col justify-center">
            <p className="text-slate-400 text-base leading-relaxed max-w-md mb-8">
              Yeni bir proje, iş birliği ya da sadece merhaba demek için — sosyal kanallardan bize
              ulaşın.
            </p>
            <div className="flex flex-wrap gap-3">
              {contact.socials.map((s) => {
                const Icon = icons[s.id];
                return (
                  <motion.a
                    key={s.id}
                    data-testid={`contact-social-${s.id}`}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                    className="inline-flex items-center gap-2.5 rounded-full border border-turq/25 bg-elevated/60 px-5 py-2.5 text-sm text-slate-200 hover:border-glow/60 hover:text-glow transition-colors"
                  >
                    <Icon size={16} className="text-turq" />
                    {s.label}
                  </motion.a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <footer className="mt-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <span className="bg-white rounded-full px-3 py-1.5">
            <img src={LOGO_URL} alt="iCONTENT" className="h-6 w-auto" />
          </span>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
            © iCONTENT — Vision, Expertise and Branded Content
          </p>
        </div>
      </footer>
    </section>
  );
}
